import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DetailView } from '@pro4-erp/core-web';
import { enqueueSnackbar } from 'notistack';
import { createAuditorCompetence } from '../../api/createAuditorCompetence';
import AuditorCompetenceForm from '../../components/Form';
import { useCreatePageStore } from './useCreatePageStore';
import FilesCreate from './FileCreate';
import { getMessageFromError } from '@/core';

const CreatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createPageStore = useCreatePageStore();
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: createAuditorCompetence,
    onSuccess: ({ id }) => {
      enqueueSnackbar(
        t('Successfully created new auditor competence.', {
          defaultValue: 'Successfully created new auditor competence.',
        }),
        { variant: 'success' }
      );
      queryClient.invalidateQueries(['auditor-competences']);
      navigate(`/auditor-competences/`);
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  useEffect(() => {
    createPageStore.reset();
    return () => {
      createPageStore.reset();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ mt: 1 }}>
      <DetailView
        childrenSx={{ padding: '2rem' }}
        title={
          <DetailView.Title>
            {t('Create new auditor competence', {
              defaultValue: 'Create new auditor competence',
            }).toString()}
          </DetailView.Title>
        }
        onGoBack={() => navigate('/auditor-competences')}
        tabs={[
          {
            id: 'details',
            title: t('Details', { defaultValue: 'Details' }),
          },
          {
            id: 'files',
            title: t('Files', { defaultValue: 'Files' }),
          },
        ]}
        defaultTab="details"
      >
        {({ TabContent }) => (
          <>
            <TabContent tab="details">
              <AuditorCompetenceForm
                onSubmit={(data) =>
                  mutate({
                    data: {
                      ...data,
                      compTypeId: data.compType?.id,
                    },
                    files: createPageStore.files,
                  })
                }
                initialState={createPageStore.form}
                onUnmount={(form) => createPageStore.setForm({ ...form })}
                isFormEditable
              />
            </TabContent>
            <TabContent tab="files">
              <FilesCreate />
            </TabContent>
          </>
        )}
      </DetailView>
    </Box>
  );
};

export default CreatePage;
